import React, { useMemo, useState } from 'react';

import { faChevronRight, faUserPlus } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, Card, Col, Modal, Nav, Row, Spinner, Tab } from 'react-bootstrap-5';
import Ownership from '../../call_list/owners/ownership';
import OwnerNameDisplay from './owner_name_display';
import Paginator from '../../paginator';
import type { Ownerships } from 'bundles/connect/types';
import RethinkMode from '../../rethink_mode/rethink_mode';
import { pushContactToRethink } from 'bundles/connect/helpers/prospect_api';
import { displayToast } from 'bundles/connect/helpers/display_toast';

type Props = {
  activeKey?: string;
  owners: Ownerships[];
  setActiveKey: (key: string) => void;
  insightsPropertyId: string;
}

export default function TabbedOwnerList({ activeKey, owners, setActiveKey, insightsPropertyId }: Props) {
  const OWNER_PAGE_SIZE = 10;
  const [page, setPage] = useState(1);
  const [showRethinkAddContactModal, setShowRethinkAddContactModal] = useState(false);
  const [ownerId, setOwnerId] = useState<number | undefined>(undefined);
  const [loading, setLoading] = useState(false);

  const paginatedOwners = useMemo(() => {
    return owners.slice((page - 1) * OWNER_PAGE_SIZE, page * OWNER_PAGE_SIZE);
  }, [owners, page]);

  const handleAddRethinkContactClick = (id: number) => {
    setOwnerId(id);
    setShowRethinkAddContactModal(true);
  };

  const handleSaveRethinkContact = () => {
    if (ownerId) {
      setLoading(true);
      pushContactToRethink({ insightsPropertyId, ownerId })
        .then(() => {
          displayToast('Contact successfully added', 'success');
          setShowRethinkAddContactModal(false);
        }).finally(() => {
          setLoading(false);
        });
    } else {
      setShowRethinkAddContactModal(false);
    }
  };

  return (
    <Tab.Container activeKey={activeKey}>
      <Row>
        <Col>
          <Nav className="nav-pills flex-column">
            {paginatedOwners.map(owner => (
              <div
                className="text-start rounded-1 border-0 nav-link"
                key={owner.id}
                onClick={() => setActiveKey(String(owner.id))}
              >
                <Row>
                  <Col>
                    <OwnerNameDisplay owner={owner} />
                  </Col>
                  <Col className="d-flex align-items-center" xs="auto">
                    <RethinkMode display>
                      <Button
                        className="ms-auto squared me-2"
                        disabled={loading}
                        variant="text-primary"
                        onClick={() => handleAddRethinkContactClick(owner.id)}
                      >
                        <FontAwesomeIcon icon={faUserPlus} />
                      </Button>
                    </RethinkMode>
                    <FontAwesomeIcon icon={faChevronRight} />
                  </Col>
                </Row>
              </div>
            ))}
          </Nav>
          {owners.length > OWNER_PAGE_SIZE && (
            <div className="d-flex justify-content-center">
              <Paginator currentPage={page} perPage={OWNER_PAGE_SIZE} total={owners.length} onSelect={setPage} />
            </div>
          )}
        </Col>
        <Col>
          <Card>
            <Card.Body>
              <Tab.Content>
                {owners.map(owner => (
                  <Tab.Pane eventKey={owner.id} key={owner.id}>
                    <Ownership owner={owner} showOwnerName={true} />
                  </Tab.Pane>
                ))}
              </Tab.Content>
            </Card.Body>
          </Card>
        </Col>
      </Row>
      <RethinkMode display>
        <Modal show={showRethinkAddContactModal} onHide={() => setShowRethinkAddContactModal(false)}>
          <Modal.Header closeButton>
            <Modal.Title>Save Contact</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            Adding a contact will automatically associate it with the property.
            If a property does not exist, it will get created.
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleSaveRethinkContact}>
              {loading ? (
                <>
                  <Spinner animation="border" size="sm" />
                  <span>Saving ...</span>
                </>
              ) : 'Save Contact'}
            </Button>
          </Modal.Footer>
        </Modal>
      </RethinkMode>
    </Tab.Container>
  );
}
